/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  combineReducers,
  composeReducers,
  composeSetters,
  fieldValidationSetter,
  FormStateSnapshot,
  formStateSnapshot,
  markFormStateAsTouched,
  onChangeReducer,
  setDirty,
  setEntityField,
  setEntityFields,
  toOnChangeReducer,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { first } from 'lodash';
import {
  complementJobCsDetailsCc,
  complementJobCsDetailsItem,
  getJobCsDetailsHeadingId,
  getJobCsDetailsItemId,
  getJobCsDetailsSubheadingId,
  jobCsCostCenterArr,
  JobCsDetails,
  JobCsDetailsCc,
  JobCsDetailsHeading,
  JobCsDetailsItem,
  JobCsDetailsMasterInfo,
  jobCsDetailsMasterInfoFromModel,
  JobCsDetailsSubheading,
  jobCsHeadingArr,
  jobCsItemArr,
  jobCsSubheadingArr,
  mergeHeadings,
  validateJobCsDetailsCcField,
  validateJobCsDetailsHeadingField,
  validateJobCsDetailsItemField,
  validateJobCsDetailsSubheadingField,
} from '../../resources/job-cs-details.model';
import {
  getInvalidPathFocusedCell,
  getInvalidPathFocusedTable,
  JobCsDetailsFocusedTable,
  JobCsDetailsSelection,
  JobCsDetailsSettings,
  JobCsDetailsState,
  JobCsDetailsStateEvent,
  JobCsDetailsStateEventType,
  queryJobCsCurrentItem,
  queryJobCsCurrentSubheading,
  queryJobCsHeading,
  queryJobCsInvalidPath,
  queryJobCsMasterInfo,
  queryJobCsMasterInfoHasErrors,
  initJobCsHistory,
  addJobCsToHistory,
  undoJobCs,
  redoJobCs,
} from './job-cs-details.state';

const setJobCsHeadingField = composeSetters(
  setEntityField,
  fieldValidationSetter(validateJobCsDetailsHeadingField),
  setDirty);

const setJobCsSubheadingField = composeSetters(
  setEntityField,
  fieldValidationSetter(validateJobCsDetailsSubheadingField),
  setDirty);

const setJobCsItemField = composeSetters(
  setEntityField,
  fieldValidationSetter(validateJobCsDetailsItemField),
  setDirty);

const setJobCsQcField = composeSetters(
  setEntityField,
  setDirty);

const setJobCsCcField = composeSetters(
  (entity: any, field: keyof JobCsDetailsCc, value) => {
    if (field === 'costCenter') {
      // propagate setting of costCenter field to another JobCsDetailsCc fields
      if (value) {
        if (value.id !== entity.costCenterId) {
          return setEntityFields(entity, {
            costCenterId: value.id,
            code: value.name,
            description: value.description,
            costCenter: {
              id: value.id,
              name: value.name,
              description: value.description,
              display: value.name,
            },
          });
        } else {
          return entity;
        }
      } else {
        return setEntityFields(entity, { costCenterId: void 0, code: void 0, description: void 0, costCenter: void 0 });
      }
    } else {
      return setEntityField(entity, field, value);
    }
  },
  fieldValidationSetter(validateJobCsDetailsCcField),
  setDirty);

const mainFlowReducer = onChangeReducer(
  composeReducers(
    combineReducers({
      settings: settingsReducer,
      masterInfo: masterInfoReducer,
      headings: headingsReducer,
      quoteChanges: quoteChangeHeadingReducer,
    }),
    selectionReducer,
  ),
  toOnChangeReducer(globalFlagsReducer),
  historyReducer,
);

function historyReducer(oldState: JobCsDetailsState, newState: JobCsDetailsState, event: JobCsDetailsStateEvent): JobCsDetailsState {
  switch (event.type) {
    case JobCsDetailsStateEventType.SetMasterInfo: {
      if (newState.hasOnlyContactMenuChanges) {
        return newState;
      }
      const resetContactFlagState = setEntityField(newState, 'hasOnlyContactMenuChanges', false);
      const stateWithHistory = addJobCsToHistory(resetContactFlagState);
      return setEntityFields(resetContactFlagState, {
        history: stateWithHistory.history,
        historyPosition: stateWithHistory.historyPosition,
      });
    }
    case JobCsDetailsStateEventType.HeadingAdd:
    case JobCsDetailsStateEventType.HeadingSetField:
    case JobCsDetailsStateEventType.HeadingRemove:
    case JobCsDetailsStateEventType.HeadingMove:
    case JobCsDetailsStateEventType.SubheadingAdd:
    case JobCsDetailsStateEventType.SubheadingRemove:
    case JobCsDetailsStateEventType.SubheadingMove:
    case JobCsDetailsStateEventType.SubheadingSetField:
    case JobCsDetailsStateEventType.ItemAdd:
    case JobCsDetailsStateEventType.ItemRemove:
    case JobCsDetailsStateEventType.ItemMove:
    case JobCsDetailsStateEventType.ItemSetField:
    case JobCsDetailsStateEventType.MoveToItems:
    case JobCsDetailsStateEventType.BackToQuoteChanges:
    case JobCsDetailsStateEventType.CostCenterAdd:
    case JobCsDetailsStateEventType.CostCenterRemove:
    case JobCsDetailsStateEventType.CostCenterSetField:
    case JobCsDetailsStateEventType.QuoteChangeSetField: {
      // Reset contact menu changes flag when other changes are made
      const resetFlagState = setEntityField(newState, 'hasOnlyContactMenuChanges', false);
      // Append snapshot of current (post-mutation) state; same model as spec addToHistory after mutation.
      const stateWithHistoryForOther = addJobCsToHistory(resetFlagState);
      return setEntityFields(resetFlagState, {
        history: stateWithHistoryForOther.history,
        historyPosition: stateWithHistoryForOther.historyPosition,
      });
    }
    default:
      return newState;
  }
}

export function jobCsDetailsReducer(state: JobCsDetailsState, event: JobCsDetailsStateEvent): JobCsDetailsState {  
  switch (event.type) {
    case JobCsDetailsStateEventType.Load: {
      const loadedState = loadDetails(state, event.details, event.mode, event.markAsDirty);
      return initJobCsHistory(loadedState);
    }
    case JobCsDetailsStateEventType.Merge:
      return mergeDetails(state, event.details);
    case JobCsDetailsStateEventType.InitHistory:
      return initJobCsHistory(state);
    case JobCsDetailsStateEventType.AddToHistory:
      return addJobCsToHistory(state);
    case JobCsDetailsStateEventType.Undo: {
      const undoState = undoJobCs(state);
      return setEntityField(undoState, 'hasOnlyContactMenuChanges', false);
    }
    case JobCsDetailsStateEventType.Redo: {
      const redoState = redoJobCs(state);
      return setEntityField(redoState, 'hasOnlyContactMenuChanges', false);
    }
    case JobCsDetailsStateEventType.MarkContactMenuChange:
      return setEntityField(state, 'hasOnlyContactMenuChanges', true);
    case JobCsDetailsStateEventType.SettingSetField:
    case JobCsDetailsStateEventType.SelectHeading:
    case JobCsDetailsStateEventType.SelectSubheading:
    case JobCsDetailsStateEventType.SelectItem:
    case JobCsDetailsStateEventType.NavigateToInvalid:
    case JobCsDetailsStateEventType.ResetFocusedCell:
    case JobCsDetailsStateEventType.SetMasterInfo:
    case JobCsDetailsStateEventType.HeadingAdd:
    case JobCsDetailsStateEventType.HeadingSetField:
    case JobCsDetailsStateEventType.HeadingRemove:
    case JobCsDetailsStateEventType.HeadingMove:
    case JobCsDetailsStateEventType.SubheadingAdd:
    case JobCsDetailsStateEventType.SubheadingRemove:
    case JobCsDetailsStateEventType.SubheadingMove:
    case JobCsDetailsStateEventType.SubheadingSetField:
    case JobCsDetailsStateEventType.ItemAdd:
    case JobCsDetailsStateEventType.ItemRemove:
    case JobCsDetailsStateEventType.ItemMove:
    case JobCsDetailsStateEventType.ItemSetField:
    case JobCsDetailsStateEventType.MoveToItems:
    case JobCsDetailsStateEventType.BackToQuoteChanges:
    case JobCsDetailsStateEventType.CostCenterAdd:
    case JobCsDetailsStateEventType.CostCenterRemove:
    case JobCsDetailsStateEventType.CostCenterSetField:
    case JobCsDetailsStateEventType.QuoteChangeSetField:
      return mainFlowReducer(state, event);
    default:
      return state;
  }
}

function settingsReducer(state: JobCsDetailsSettings, event: JobCsDetailsStateEvent): JobCsDetailsSettings {
  switch (event.type) {
    case JobCsDetailsStateEventType.SettingSetField:
      return setEntityField(state, event.name, event.value);
    default:
      return state;
  }
}

function masterInfoReducer(masterInfo: FormStateSnapshot<JobCsDetailsMasterInfo>,
                           event: JobCsDetailsStateEvent): FormStateSnapshot<JobCsDetailsMasterInfo> {
  switch (event.type) {
    case JobCsDetailsStateEventType.SetMasterInfo:
      return event.masterInfo;
    default:
      return masterInfo;
  }
}

function headingsReducer(headings: JobCsDetailsHeading[], event: JobCsDetailsStateEvent): JobCsDetailsHeading[] {
  switch (event.type) {
    case JobCsDetailsStateEventType.HeadingAdd:
      return event.afterId
        ? jobCsHeadingArr.insert(headings, jobCsHeadingArr.indexOf(headings, event.afterId) + 1, event.entity)
        : jobCsHeadingArr.prepend(headings, event.entity);
    case JobCsDetailsStateEventType.HeadingRemove:
      return jobCsHeadingArr.remove(headings, event.id);
    case JobCsDetailsStateEventType.HeadingMove:
      return jobCsHeadingArr.move(
        headings,
        jobCsHeadingArr.indexOf(headings, event.fromId),
        jobCsHeadingArr.indexOf(headings, event.toId));
    case JobCsDetailsStateEventType.HeadingSetField:
      return jobCsHeadingArr.merge(
        headings,
        event.id,
        (heading) => setJobCsHeadingField(heading, event.name, event.value));
    case JobCsDetailsStateEventType.SubheadingAdd:
    case JobCsDetailsStateEventType.SubheadingRemove:
    case JobCsDetailsStateEventType.SubheadingMove:
    case JobCsDetailsStateEventType.SubheadingSetField:
    case JobCsDetailsStateEventType.ItemAdd:
    case JobCsDetailsStateEventType.ItemRemove:
    case JobCsDetailsStateEventType.ItemMove:
    case JobCsDetailsStateEventType.ItemSetField:
    case JobCsDetailsStateEventType.MoveToItems:
    case JobCsDetailsStateEventType.BackToQuoteChanges:
    case JobCsDetailsStateEventType.CostCenterAdd:
    case JobCsDetailsStateEventType.CostCenterRemove:
    case JobCsDetailsStateEventType.CostCenterSetField:
      return jobCsHeadingArr.merge(
        headings,
        event.headingId,
        (heading) => setJobCsHeadingField(heading, 'subHeadings', subheadingsReducer(heading.subHeadings, event)));
    default:
      return headings;
  }
}

function subheadingsReducer(subheadings: JobCsDetailsSubheading[],
                            event: JobCsDetailsStateEvent): JobCsDetailsSubheading[] {
  switch (event.type) {
    case JobCsDetailsStateEventType.SubheadingAdd:
      return event.afterId
        ? jobCsSubheadingArr.insert(
          subheadings,
          jobCsSubheadingArr.indexOf(subheadings, event.afterId) + 1, event.entity)
        : jobCsSubheadingArr.prepend(subheadings, event.entity);
    case JobCsDetailsStateEventType.SubheadingRemove:
      return jobCsSubheadingArr.remove(subheadings, event.id);
    case JobCsDetailsStateEventType.SubheadingMove:
      return jobCsSubheadingArr.move(
        subheadings,
        jobCsSubheadingArr.indexOf(subheadings, event.fromId),
        jobCsSubheadingArr.indexOf(subheadings, event.toId));
    case JobCsDetailsStateEventType.SubheadingSetField:
      return jobCsSubheadingArr.merge(
        subheadings,
        event.id,
        (subheading) => setJobCsSubheadingField(subheading, event.name, event.value));
    case JobCsDetailsStateEventType.ItemAdd:
    case JobCsDetailsStateEventType.ItemRemove:
    case JobCsDetailsStateEventType.ItemMove:
    case JobCsDetailsStateEventType.ItemSetField:
    case JobCsDetailsStateEventType.MoveToItems:
    case JobCsDetailsStateEventType.BackToQuoteChanges:
    case JobCsDetailsStateEventType.CostCenterAdd:
    case JobCsDetailsStateEventType.CostCenterRemove:
    case JobCsDetailsStateEventType.CostCenterSetField:
      return jobCsSubheadingArr.merge(
        subheadings,
        event.subheadingId,
        (subheading) =>
          setJobCsSubheadingField(subheading, 'items', itemsReducer(subheading, subheading.items, event)));
    default:
      return subheadings;
  }
}

function itemsReducer(subheading: JobCsDetailsSubheading,
                      items: JobCsDetailsItem[],
                      event: JobCsDetailsStateEvent): JobCsDetailsItem[] {
  switch (event.type) {
    case JobCsDetailsStateEventType.ItemAdd:
      return jobCsItemArr.prepend(items, complementJobCsDetailsItem(event.entity));
    case JobCsDetailsStateEventType.ItemRemove:
      return jobCsItemArr.remove(items, event.id);
    case JobCsDetailsStateEventType.ItemMove:
      return jobCsItemArr.move(
        items,
        jobCsItemArr.indexOf(items, event.fromId),
        jobCsItemArr.indexOf(items, event.toId));
    case JobCsDetailsStateEventType.ItemSetField:
      return jobCsItemArr.merge(
        items,
        event.id,
        (item) => setJobCsItemField(item, event.name, event.value));
    case JobCsDetailsStateEventType.MoveToItems: {
      const qc = setEntityField(event.quoteChange, 'isHidden', false);
      return jobCsItemArr.prepend(
        items,
        complementJobCsDetailsItem(setEntityField(qc, 'name', subheading.name)));
    }
    case JobCsDetailsStateEventType.BackToQuoteChanges:
      return jobCsItemArr.remove(items, getJobCsDetailsItemId(event.item));
    case JobCsDetailsStateEventType.CostCenterAdd:
    case JobCsDetailsStateEventType.CostCenterRemove:
    case JobCsDetailsStateEventType.CostCenterSetField:
      return jobCsItemArr.merge(
        items,
        event.itemId,
        (item) => setJobCsItemField(item, 'costCenters', costCentersReducer(item.costCenters, event)));
    default:
      return items;
  }
}

function costCentersReducer(costCenters: JobCsDetailsCc[],
                            event: JobCsDetailsStateEvent): JobCsDetailsCc[] {
  switch (event.type) {
    case JobCsDetailsStateEventType.CostCenterAdd:
      return jobCsCostCenterArr.prepend(costCenters, ...event.entities.map(complementJobCsDetailsCc));
    case JobCsDetailsStateEventType.CostCenterRemove:
      return jobCsCostCenterArr.remove(costCenters, event.id);
    case JobCsDetailsStateEventType.CostCenterSetField:
      return jobCsCostCenterArr.merge(
        costCenters,
        event.id,
        (item) => setJobCsCcField(item, event.name, event.value));
    default:
      return costCenters;
  }
}

function quoteChangeHeadingReducer(quoteChanges: JobCsDetailsHeading,
                                   event: JobCsDetailsStateEvent): JobCsDetailsHeading {
  switch (event.type) {
    case JobCsDetailsStateEventType.MoveToItems:
    case JobCsDetailsStateEventType.BackToQuoteChanges:
    case JobCsDetailsStateEventType.QuoteChangeSetField: {
      const subheading = first(quoteChanges.subHeadings);
      if (subheading) {
        // remove item from subheading:
        // this way we have to walk through the following path: heading -> subheadings -> subheading -> items
        return setEntityField(
          quoteChanges,
          'subHeadings',
          jobCsSubheadingArr.update(
            quoteChanges.subHeadings,
            getJobCsDetailsSubheadingId(subheading),
            setEntityField(
              subheading,
              'items',
              quoteChangeItemReducer(subheading.items, event))));
      } else {
        return quoteChanges;
      }
    }
    default:
      return quoteChanges;
  }
}

function quoteChangeItemReducer(items: JobCsDetailsItem[],
                                event: JobCsDetailsStateEvent): JobCsDetailsItem[] {
  switch (event.type) {
    case JobCsDetailsStateEventType.MoveToItems:
      return jobCsItemArr.remove(items, getJobCsDetailsItemId(event.quoteChange));
    case JobCsDetailsStateEventType.BackToQuoteChanges:
      return jobCsItemArr.insertSorted(items, event.item, 'name');
    case JobCsDetailsStateEventType.QuoteChangeSetField:
      return jobCsItemArr.merge(
        items,
        event.id,
        (item) => setJobCsQcField(item, event.name, event.value));
    default:
      return items;
  }
}

function globalFlagsReducer(state: JobCsDetailsState, event: JobCsDetailsStateEvent): JobCsDetailsState {
  switch (event.type) {
    case JobCsDetailsStateEventType.SetMasterInfo:
    case JobCsDetailsStateEventType.HeadingAdd:
    case JobCsDetailsStateEventType.HeadingSetField:
    case JobCsDetailsStateEventType.HeadingRemove:
    case JobCsDetailsStateEventType.HeadingMove:
    case JobCsDetailsStateEventType.SubheadingAdd:
    case JobCsDetailsStateEventType.SubheadingRemove:
    case JobCsDetailsStateEventType.SubheadingMove:
    case JobCsDetailsStateEventType.SubheadingSetField:
    case JobCsDetailsStateEventType.ItemAdd:
    case JobCsDetailsStateEventType.ItemRemove:
    case JobCsDetailsStateEventType.ItemMove:
    case JobCsDetailsStateEventType.ItemSetField:
    case JobCsDetailsStateEventType.MoveToItems:
    case JobCsDetailsStateEventType.BackToQuoteChanges:
    case JobCsDetailsStateEventType.CostCenterAdd:
    case JobCsDetailsStateEventType.CostCenterRemove:
    case JobCsDetailsStateEventType.CostCenterSetField:
    case JobCsDetailsStateEventType.QuoteChangeSetField:
      return setDirty(state);
    default:
      return state;
  }
}

function selectionReducer(state: JobCsDetailsState, event: JobCsDetailsStateEvent): JobCsDetailsState {
  const { selection } = state;
  switch (event.type) {
    case JobCsDetailsStateEventType.HeadingRemove:
      return setEntityField(state, 'selection', selectHeading(selection));
    case JobCsDetailsStateEventType.SubheadingRemove:
      return setEntityField(state, 'selection', selectSubheading(selection));
    case JobCsDetailsStateEventType.ItemRemove:
    case JobCsDetailsStateEventType.BackToQuoteChanges:
      return setEntityField(state, 'selection', selectItem(selection));
    case JobCsDetailsStateEventType.SelectHeading:
      return setEntityField(
        state,
        'selection',
        selectHeading(selection, event.headingId ? queryJobCsHeading(state, event.headingId) : void 0));
    case JobCsDetailsStateEventType.SelectSubheading: {
      // we create preselected state here,
      // because later we have to find "current" subheading relative to selected heading
      const preselectedState = setEntityField(state, 'selection', selectHeadingById(selection, event.headingId));
      return setEntityField(
        preselectedState,
        'selection',
        selectSubheading(
          preselectedState.selection,
          event.subheadingId ? queryJobCsCurrentSubheading(preselectedState, event.subheadingId) : void 0));
    }
    case JobCsDetailsStateEventType.SelectItem: {
      // we create preselected state here,
      // because later we have to find "current" item relative to selected heading and subheading
      const preselectedState = setEntityField(
        state,
        'selection',
        selectSubheadingById(selectHeadingById(selection, event.headingId), event.subheadingId));
      return setEntityField(
        preselectedState,
        'selection',
        selectItem(
          preselectedState.selection,
          event.itemId ? queryJobCsCurrentItem(preselectedState, event.itemId) : void 0));
    }
    case JobCsDetailsStateEventType.NavigateToInvalid:
      return navigateToInvalid(state);
    case JobCsDetailsStateEventType.ResetFocusedCell:
      return setEntityField(state, 'selection', focusTableCell(state.selection));
    default:
      return state;
  }
}

function navigateToInvalid(state: JobCsDetailsState): JobCsDetailsState {
  if (queryJobCsMasterInfoHasErrors(state)) {
    return setEntityField(state, 'masterInfo', markFormStateAsTouched(queryJobCsMasterInfo(state)));
  }

  const invalidPath = queryJobCsInvalidPath(state);

  if (invalidPath) {
    // route to invalid entity
    const invalidRoute = invalidPath.slice(0, invalidPath.length - 1);
    const heading = invalidRoute[0];
    const subheading = invalidRoute[1];
    const item = invalidRoute[2];
    // update selection
    let nextSelection = state.selection;
    nextSelection = selectHeading(nextSelection, heading);
    nextSelection = selectSubheading(nextSelection, subheading);
    nextSelection = selectItem(nextSelection, item);
    // focus invalid cell
    return setEntityField(state, 'selection', focusTableCell(
      nextSelection,
      getInvalidPathFocusedTable(invalidPath),
      getInvalidPathFocusedCell(invalidPath)));
  } else {
    return state;
  }
}

const selectHeadingById = (selection: JobCsDetailsSelection, headingId?: ID) =>
  setEntityField(selection, 'headingId', headingId);
const selectSubheadingById = (selection: JobCsDetailsSelection, subheadingId?: ID) =>
  setEntityField(selection, 'subheadingId', subheadingId);
const selectItemById = (selection: JobCsDetailsSelection, itemId?: ID) =>
  setEntityField(selection, 'itemId', itemId);

function selectHeading(selection: JobCsDetailsSelection, heading?: JobCsDetailsHeading): JobCsDetailsSelection {
  const subheading = heading ? first(heading.subHeadings) : void 0;
  const headingId = heading && getJobCsDetailsHeadingId(heading);
  return headingId === selection.headingId
    ? selection
    : selectSubheading(selectHeadingById(selection, headingId), subheading);
}

function selectSubheading(selection: JobCsDetailsSelection,
                          subheading?: JobCsDetailsSubheading): JobCsDetailsSelection {
  const item = subheading ? first(subheading.items) : void 0;
  const subheadingId = subheading && getJobCsDetailsSubheadingId(subheading);
  return subheadingId === selection.subheadingId
    ? selection
    : selectItem(selectSubheadingById(selection, subheadingId), item);
}

function selectItem(selection: JobCsDetailsSelection, item?: JobCsDetailsItem): JobCsDetailsSelection {
  return selectItemById(selection, item && getJobCsDetailsItemId(item));
}

function focusTableCell(selection: JobCsDetailsSelection,
                        focusedTable?: JobCsDetailsFocusedTable,
                        focusedCell?: FocusedCell): JobCsDetailsSelection {
  return setEntityFields(selection, { focusedTable, focusedCell });
}

function loadDetails(state: JobCsDetailsState,
                     details: JobCsDetails,
                     formMode?: FormMode,
                     markAsDirty?: boolean): JobCsDetailsState {                      
  return {
    ...state,
    formMode: formMode || state.formMode,
    jobId: details.jobId,
    csMasterId: details.csMasterId,
    masterInfo: formStateSnapshot(jobCsDetailsMasterInfoFromModel(details)),
    headings: details.headings,
    quoteChanges: details.quoteChanges,
    isDirty: markAsDirty || false,
    version: details.version,
    selection: setSelectionObject(state.selection, details),
    hadEmptyItems: details.hadEmptyItems,
    settings: {
      ccClone: details.isJobCsCostCenterClone,
      ccRequired: details.isJobCsCostCenterRequired,
    },
  };
}

function setSelectionObject(selection: JobCsDetailsSelection, details: JobCsDetails): JobCsDetailsSelection {
  let newSelection = selection;
  // set heading
  const heading = details.headings.length > 0 ? details.headings[0] : void 0;
  newSelection = selectHeading(newSelection, heading);

  // set subheading
  const subheading = heading && heading.subHeadings.length > 0 ? heading.subHeadings[0] : void 0;
  newSelection = selectSubheading(newSelection, subheading);

  // set item
  const item = subheading && subheading.items.length > 0 ? subheading.items[0] : void 0;
  newSelection = selectItem(newSelection, item);  
  return newSelection;
}

function mergeDetails(state: JobCsDetailsState, details: JobCsDetails): JobCsDetailsState {
  return {
    ...state,
    headings: mergeHeadings(state.headings, details.headings),
    quoteChanges: details.quoteChanges,
    isDirty: false,
    version: details.version,
    hadEmptyItems: details.hadEmptyItems,
  };
}
